import React from "react";
import { Chip, Typography } from "@/components/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faGlobe,
  faTag,
  faToggleOn,
  faToggleOff,
  faFileImport,
  faList,
  faLayerGroup,
  faCheckCircle,
  faSpinner,
  faExclamationCircle,
  faTimesCircle,
  faClock,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { SourceIcon } from "@/components/resource-card";

export interface GroupHeaderProps<T extends string = string> {
  groupKey: string;
  groupCount: number;
  groupBy: T;
  isCollapsed: boolean;
  onToggle: () => void;
  /**
   * Optional custom icon resolver function.
   * If not provided, uses default icon mapping based on resource type.
   */
  iconResolver?: (groupBy: T, groupKey: string) => IconDefinition | null;
  /**
   * Whether to show SourceIcon for certain groupBy values.
   * Only applies to tracker "source" groupBy by default.
   */
  showSourceIcon?: boolean;
  /**
   * Default text when groupKey is empty.
   */
  emptyGroupLabel?: string;
  /**
   * Resource type for default icon mapping.
   */
  resourceType?: "tracker" | "enrichment" | "alert" | "dashboard" | "widget";
}

// Default icon mappings for different resource types
const DEFAULT_ICON_MAP: Record<string, Record<string, IconDefinition | ((key: string) => IconDefinition)>> = {
  tracker: {
    source: () => faGlobe, // Special case - uses SourceIcon
    tags: () => faTag,
    status: (key: string) => key === "Actifs" ? faToggleOn : faToggleOff,
    type: (key: string) => key === "Import" ? faFileImport : faList,
  },
  enrichment: {
    type: () => faTag,
    status: (key: string) => {
      switch (key) {
        case "Terminé":
        case "Actifs":
          return faCheckCircle;
        case "En cours":
          return faSpinner;
        case "Échec":
        case "Erreur":
          return faExclamationCircle;
        case "Annulé":
          return faTimesCircle;
        case "En attente":
        case "Inactifs":
          return faClock;
        default:
          return faLayerGroup;
      }
    },
  },
  alert: {
    type: () => faTag,
    status: (key: string) => key === "Activées" ? faToggleOn : faToggleOff,
  },
  dashboard: {
    recent: () => faClock,
    widgets_count: () => faLayerGroup,
  },
  widget: {
    type: () => faLayerGroup,
    dashboard: () => faLayerGroup,
  },
};

/**
 * Generic GroupHeader component for displaying collapsible group headers
 * in resource galleries.
 * 
 * @example
 * ```tsx
 * // For trackers with source icons
 * <GroupHeader
 *   groupKey="reddit"
 *   groupCount={5}
 *   groupBy="source"
 *   isCollapsed={false}
 *   onToggle={() => toggleGroup("reddit")}
 *   resourceType="tracker"
 *   showSourceIcon
 * />
 * 
 * // For enrichments with custom icon resolver
 * <GroupHeader
 *   groupKey="Actifs"
 *   groupCount={10}
 *   groupBy="status"
 *   isCollapsed={false}
 *   onToggle={() => toggleGroup("Actifs")}
 *   iconResolver={(groupBy, key) => key === "Actifs" ? faCheckCircle : faLayerGroup}
 * />
 * ```
 */
function GroupHeader<T extends string = string>({
  groupKey,
  groupCount,
  groupBy,
  isCollapsed,
  onToggle,
  iconResolver,
  showSourceIcon = true,
  emptyGroupLabel = "Tous",
  resourceType = "tracker",
}: GroupHeaderProps<T>) {
  const getDefaultIcon = (): IconDefinition => {
    // Use custom resolver if provided
    if (iconResolver) {
      const icon = iconResolver(groupBy, groupKey);
      if (icon) return icon;
    }

    // Use default mapping based on resource type
    const resourceMap = DEFAULT_ICON_MAP[resourceType];
    if (resourceMap && resourceMap[groupBy]) {
      const iconOrResolver = resourceMap[groupBy];
      if (typeof iconOrResolver === "function") {
        return iconOrResolver(groupKey);
      }
      return iconOrResolver;
    }

    // Default fallback
    return faLayerGroup;
  };

  // Special handling for tracker source with SourceIcon
  const shouldShowSourceIcon = showSourceIcon && 
    resourceType === "tracker" && 
    groupBy === "source";

  return (
    <div
      className="flex items-center gap-3 p-3 bg-surface-secondary rounded-arc-lg cursor-pointer hover:bg-surface-tertiary transition-colors duration-arc-fast border border-arc-border"
      onClick={onToggle}
    >
      <FontAwesomeIcon
        icon={isCollapsed ? faChevronRight : faChevronDown}
        className="text-content-secondary"
        size="sm"
      />
      {shouldShowSourceIcon ? (
        <SourceIcon source={groupKey} size="sm" />
      ) : (
        <FontAwesomeIcon
          icon={getDefaultIcon()}
          className="text-content-secondary"
          size="sm"
        />
      )}
      <Typography
        variant="h6"
        className="text-base font-semibold text-content-primary flex-1"
      >
        {groupKey || emptyGroupLabel}
      </Typography>
      <Chip
        label={String(groupCount)}
        size="sm"
        className="bg-surface-tertiary text-content-primary font-medium"
      />
    </div>
  );
}

GroupHeader.displayName = "GroupHeader";

export default GroupHeader;